/**
 * Client Registration Code Utilities
 *
 * Provides functions for generating, formatting, validating, and managing
 * client registration codes in the monelab system.
 */

import type { Client } from '@/types/contacts';

/**
 * Configuration constants for registration codes
 */
export const REGISTRATION_CODE_CONFIG = {
  prefix: 'CA',
  separator: '-',
  minSuffixLength: 6,
  maxSuffixLength: 8,
  alphabet: '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ',
} as const;

/**
 * Generate a unique registration code for a client
 * Format: CA-XXXXXX (where X is alphanumeric)
 *
 * @returns A unique registration code string
 * @throws Error if code generation fails after max attempts
 */
export function generateRegistrationCode(): string {
  const { prefix, separator, minSuffixLength, maxSuffixLength, alphabet } = REGISTRATION_CODE_CONFIG;
  const suffixLength = minSuffixLength + Math.floor(Math.random() * (maxSuffixLength - minSuffixLength + 1));

  let code = '';
  for (let i = 0; i < suffixLength; i++) {
    code += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
  }

  return `${prefix}${separator}${code}`;
}

/**
 * Format a registration code for display (uppercase, with proper spacing)
 *
 * @param code The registration code to format
 * @returns Formatted registration code
 */
export function formatRegistrationCode(code?: string | null): string {
  if (!code) return '';

  const trimmed = code.trim().toUpperCase();

  // Validate format and fix common issues
  const match = trimmed.match(/^CA[-\s]?([A-Z0-9]+)$/i);
  if (match) {
    return `CA-${match[1]}`;
  }

  return trimmed;
}

/**
 * Validate registration code format
 *
 * @param code The registration code to validate
 * @returns True if valid format, false otherwise
 */
export function isValidRegistrationCode(code?: string | null): boolean {
  if (!code) return false;

  const formatted = formatRegistrationCode(code);
  const pattern = /^CA-[A-Z0-9]{6,8}$/;
  return pattern.test(formatted);
}

/**
 * Validate registration code structure and provide detailed feedback
 *
 * @param code The registration code to validate
 * @returns Detailed validation result
 */
export function validateRegistrationCode(code?: string | null): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  formatted?: string;
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!code) {
    errors.push('Código de registro é obrigatório');
    return { isValid: false, errors, warnings };
  }

  const trimmed = code.trim();

  if (trimmed.length === 0) {
    errors.push('Código de registro não pode estar vazio');
    return { isValid: false, errors, warnings };
  }

  const formatted = formatRegistrationCode(code);

  // Check length
  if (formatted.length < 8) { // CA-XXXXXX minimum
    errors.push('Código de registro muito curto (mínimo 8 caracteres)');
  } else if (formatted.length > 10) { // CA-XXXXXXXX maximum
    errors.push('Código de registro muito longo (máximo 10 caracteres)');
  }

  // Check pattern
  if (!/^CA-[A-Z0-9]+$/.test(formatted)) {
    errors.push('Formato inválido. Use: CA-XXXXXX (onde X é letra ou número)');
  }

  // Check suffix length
  const suffix = formatted.split('-')[1];
  if (suffix && (suffix.length < 6 || suffix.length > 8)) {
    warnings.push('Sufixo do código deve ter entre 6 e 8 caracteres');
  }

  // Check for common typos
  if (trimmed.toLowerCase().startsWith('ca-')) {
    warnings.push('Prefixo deve ser "CA-" maiúsculo');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    formatted: errors.length === 0 ? formatted : undefined,
  };
}

/**
 * Copy registration code to clipboard
 *
 * @param code The registration code to copy
 * @returns Promise that resolves when copy is complete
 * @throws Error if clipboard access is denied or unavailable
 */
export async function copyRegistrationCode(code?: string | null): Promise<void> {
  if (!code) {
    throw new Error('Nenhum código de registro para copiar');
  }

  const formatted = formatRegistrationCode(code);

  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(formatted);
    } catch (error) {
      throw new Error('Falha ao copiar código para a área de transferência');
    }
  } else {
    // Fallback for older browsers
    try {
      const textArea = document.createElement('textarea');
      textArea.value = formatted;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const result = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (!result) {
        throw new Error('Falha ao copiar código para a área de transferência');
      }
    } catch (error) {
      throw new Error('Seu navegador não suporta cópia automática. Copie manualmente: ' + formatted);
    }
  }
}

/**
 * Extract registration code from text input
 * Handles various input formats and extracts the code
 *
 * @param input The text input to extract code from
 * @returns Extracted registration code or null if not found
 */
export function extractRegistrationCode(input?: string | null): string | null {
  if (!input) return null;

  const trimmed = input.trim();

  // Direct format
  if (isValidRegistrationCode(trimmed)) {
    return formatRegistrationCode(trimmed);
  }

  // Look for code pattern in text
  const match = trimmed.match(/CA[-\s]?([A-Z0-9]{6,8})/i);
  if (match) {
    return formatRegistrationCode(match[0]);
  }

  // Look for alphanumeric sequences that might be codes
  const alphaNumMatch = trimmed.match(/\b([A-Z0-9]{6,8})\b/i);
  if (alphaNumMatch && trimmed.toLowerCase().includes('ca')) {
    return formatRegistrationCode(`CA-${alphaNumMatch[1]}`);
  }

  return null;
}

/**
 * Search clients by registration code
 * This is a utility function that can be used with the clientsService
 *
 * @param clients Array of clients to search
 * @param searchTerm The search term (can be partial code)
 * @returns Array of matching clients
 */
export function searchClientsByRegistrationCode(
  clients: (Client & { registration_code?: string | null })[],
  searchTerm: string
): (Client & { registration_code?: string | null })[] {
  if (!searchTerm || searchTerm.trim().length === 0) {
    return [];
  }

  const trimmed = searchTerm.trim().toLowerCase();

  return clients.filter(client => {
    if (!client.registration_code) return false;

    const code = client.registration_code.toLowerCase();

    // Exact match
    if (code === trimmed) return true;

    // Contains match (allow partial searches)
    if (code.includes(trimmed)) return true;

    // Format-insensitive match
    const normalizedCode = code.replace(/[-\s]/g, '');
    const normalizedSearch = trimmed.replace(/[-\s]/g, '');

    return normalizedCode.includes(normalizedSearch);
  });
}

/**
 * Generate a batch of unique registration codes
 * Useful for bulk operations or testing
 *
 * @param count Number of codes to generate
 * @param existingCodes Array of existing codes to avoid duplicates
 * @returns Array of unique registration codes
 */
export function generateBatchRegistrationCodes(
  count: number,
  existingCodes: string[] = []
): string[] {
  const codes: string[] = [];
  const existingSet = new Set(existingCodes.map(formatRegistrationCode));
  const maxAttempts = count * 10; // Prevent infinite loops
  let attempts = 0;

  while (codes.length < count && attempts < maxAttempts) {
    const code = generateRegistrationCode();

    if (!existingSet.has(code)) {
      codes.push(code);
      existingSet.add(code);
    }

    attempts++;
  }

  if (codes.length < count) {
    throw new Error(`Failed to generate ${count} unique codes. Generated ${codes.length} codes.`);
  }

  return codes;
}

/**
 * Get registration code statistics for analytics
 *
 * @param clients Array of clients to analyze
 * @returns Statistics about registration codes
 */
export function getRegistrationCodeStats(
  clients: (Client & { registration_code?: string | null })[]
): {
  totalClients: number;
  clientsWithCode: number;
  clientsWithoutCode: number;
  codeCoveragePercentage: number;
  codesByPrefix: Record<string, number>;
} {
  const totalClients = clients.length;
  const clientsWithCode = clients.filter(c => c.registration_code).length;
  const clientsWithoutCode = totalClients - clientsWithCode;
  const codeCoveragePercentage = totalClients > 0 ? (clientsWithCode / totalClients) * 100 : 0;

  const codesByPrefix: Record<string, number> = {};

  clients.forEach(client => {
    if (client.registration_code) {
      const prefix = client.registration_code.split('-')[0];
      codesByPrefix[prefix] = (codesByPrefix[prefix] || 0) + 1;
    }
  });

  return {
    totalClients,
    clientsWithCode,
    clientsWithoutCode,
    codeCoveragePercentage,
    codesByPrefix,
  };
}

/**
 * Export registration code utilities for testing
 */
export const __testing__ = {
  generateRegistrationCode,
  formatRegistrationCode,
  isValidRegistrationCode,
  validateRegistrationCode,
  extractRegistrationCode,
  REGISTRATION_CODE_CONFIG,
};